import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "../contexts/LocalizationContext";
import { LanguageSelector } from "../components/LanguageSelector";
import type { Customer, StampStatus } from "../types";
import { useStoreSettings } from "../contexts/StoreSettingsContext";
import { fetchStampStatus, fetchMemberCoupons, fetchFeaturedInstagramPosts } from "../api/auth";
import type { Coupon } from "../types";
import type { FeaturedInstagramPost } from "../api/types";
import { routes } from "../config";
import ReceiptScanModal from "../components/ReceiptScanModal";

interface HomeProps {
  user: Customer;
  onQrClick?: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getCouponValue(coupon: Coupon) {
  if (coupon.discountType === "amount" && coupon.discountAmountCents)
    return `$${Math.round(coupon.discountAmountCents / 100)} off`;
  if (coupon.discountType === "percent" && coupon.discountPercent)
    return `${coupon.discountPercent}% off`;
  return "$0";
}

function SeeAllBtn({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        display: "inline-flex", alignItems: "center", gap: "2px",
        fontSize: "13px", fontWeight: 600, color: "#7BA88A",
        background: "none", border: "none", cursor: "pointer",
        p: 0, fontFamily: "inherit",
        "&:active": { opacity: 0.65 },
      }}
    >
      {label} <span style={{ fontSize: 15, fontWeight: 400, opacity: 0.85 }}>›</span>
    </Box>
  );
}

// ── Stamp mini-grid (matching demo exactly) ───────────────────────────────────
function StampGrid({ filled, total }: { filled: number; total: number }) {
  const perRow = total === 5 ? 5 : total === 6 ? 6 : total === 8 ? 4 : total === 10 ? 5 : total === 12 ? 6 : Math.ceil(total / 2);
  // Circle size scales down as more stamps fit per row
  const size = perRow <= 4 ? 62 : perRow <= 5 ? 52 : 40;
  const gap = perRow <= 5 ? 10 : 6;
  const checkSize = Math.round(size * 0.36);
  const stamps = Array.from({ length: total }, (_, i) => i < filled);
  return (
    <Box sx={{ width: "100%", px: "12px" }}>
    <Box sx={{
      display: "grid",
      gridTemplateColumns: `repeat(${perRow}, 1fr)`,
      gap: `${gap}px`,
      maxWidth: `calc(${perRow} * 42px + ${perRow - 1} * ${gap}px)`,
      mx: "auto",
    }}>
      {stamps.map((isFilled, i) => (
        <Box key={i} sx={{
          width: "100%", aspectRatio: "1 / 1",
          borderRadius: "50%",
          border: isFilled ? "1.5px solid #F27A3A" : "1.5px dashed #E0C4B4",
          background: isFilled ? "linear-gradient(145deg, #F27A3A 0%, #E85A22 100%)" : "rgba(255,255,255,0.7)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: Math.round(size * 0.28) + "px", fontWeight: 700,
          color: isFilled ? "#fff" : "#C9A89A",
          boxShadow: isFilled ? "0 2px 6px rgba(232,90,34,0.25)" : "none",
        }}>
          {isFilled ? (
            <svg width={checkSize} height={checkSize} viewBox="0 0 16 16" fill="none">
              <path d="M3.2 8.2l3 3.1 6.6-6.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (i + 1)}
        </Box>
      ))}
    </Box>
    </Box>
  );
}

// ── Coupon ticket (horizontal strip) ────────────────────────────────────────
function HomeCouponTicket({ coupon, onClick }: {
  coupon: Coupon; onClick: () => void
}) {
  return (
    <Box
      onClick={onClick}
      tabIndex={0}
      sx={{
        position: "relative", flexShrink: 0,
        width: 156, height: 74,
        background: "#FFF1EB",
        border: "1px solid #F3C4B0",
        borderRadius: "8px",
        display: "flex", alignItems: "stretch",
        cursor: "pointer",
        transition: "transform 0.12s ease",
        scrollSnapAlign: "start",
        overflow: "visible",
        "&:active": { transform: "scale(0.97)" },
      }}
    >
      {/* top notch */}
      <Box sx={{ position: "absolute", left: "45px", top: "-6px", width: 12, height: 12, borderRadius: "50%", backgroundColor: "#fff", zIndex: 3 }} />
      {/* bottom notch */}
      <Box sx={{ position: "absolute", left: "45px", bottom: "-6px", width: 12, height: 12, borderRadius: "50%", backgroundColor: "#fff", zIndex: 3 }} />

      {/* left stub */}
      <Box sx={{
        width: 51, display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, position: "relative", color: "#E8786A",
        "&::after": { content: '""', position: "absolute", right: 0, top: "8px", bottom: "8px", borderRight: "1.5px dashed #F0B8A8" },
      }}>
        <svg width="26" height="26" viewBox="0 0 28 28" fill="none">
          <path d="M8 6v7.5c0 1.2-.7 2-1.5 2.5V22" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          <path d="M6.5 6v6.5M9.5 6v6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          <path d="M19.5 6v16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          <path d="M19.5 6c2.8.2 4.5 2.2 4.5 5v1.5c0 2.2-1.4 3.5-4.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </Box>

      {/* right content */}
      <Box sx={{ flex: 1, padding: "12px 14px 12px 12px", display: "flex", flexDirection: "column", justifyContent: "center", gap: "5px" }}>
        <Typography sx={{ fontSize: "17px", fontWeight: 700, color: "#E8786A", lineHeight: 1.2, letterSpacing: "-0.2px", whiteSpace: "nowrap" }}>
          {getCouponValue(coupon)}
        </Typography>
        <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "#E8786A", opacity: 0.9, lineHeight: 1 }}>
          Use <span style={{ fontSize: 12, opacity: 0.75 }}>›</span>
        </Typography>
      </Box>
    </Box>
  );
}

// ── Membership card (single card, no carousel) ───────────────────────────────
function WalletPassCard({ user, stripUrl, showPoints, onQrCodeClick }: {
  user: Customer;
  stripUrl?: string;
  showPoints?: boolean;
  onQrCodeClick?: () => void;
}) {
  return (
    <Box sx={{
      position: "relative",
      width: "100%",
      height: "140px",
      borderRadius: "14px", overflow: "hidden",
      background: stripUrl
        ? `url(${stripUrl}) center/cover no-repeat`
        : "linear-gradient(135deg, #1A3525 0%, #2A5C3E 55%, #1C3A28 100%)",
      boxShadow: "0 6px 18px rgba(0,0,0,0.28)",
      padding: "14px 16px 16px",
    }}>
      {/* Overlay — uniform tint so strip image looks consistent across the full card */}
      {stripUrl && (
        <Box sx={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.38)", pointerEvents: "none" }} />
      )}

      {/* Top row: tier name + QR */}
      <Box sx={{ position: "relative", zIndex: 1, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#fff", letterSpacing: "0.3px", textShadow: "0 1px 2px rgba(0,0,0,0.2)" }}>
          {user?.nextMembershipLevel?.currentMembershipLevelName || "Member"}
        </Typography>

        {/* QR code button */}
        <Box
          onClick={onQrCodeClick}
          sx={{
            width: "38px", height: "38px",
            background: "#fff",
            borderRadius: "9px",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 2px 10px rgba(0,0,0,0.18)",
            cursor: onQrCodeClick ? "pointer" : "default",
            flexShrink: 0,
            "&:active": { opacity: 0.85 },
          }}
        >
          <svg width="26" height="26" viewBox="0 0 34 34" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="2" width="12" height="12" fill="none" stroke="#111" strokeWidth="2"/>
            <rect x="5" y="5" width="6" height="6" fill="#111"/>
            <rect x="20" y="2" width="12" height="12" fill="none" stroke="#111" strokeWidth="2"/>
            <rect x="23" y="5" width="6" height="6" fill="#111"/>
            <rect x="2" y="20" width="12" height="12" fill="none" stroke="#111" strokeWidth="2"/>
            <rect x="5" y="23" width="6" height="6" fill="#111"/>
            <rect x="18" y="18" width="4" height="4" fill="#111"/>
            <rect x="24" y="18" width="4" height="4" fill="#111"/>
            <rect x="18" y="24" width="4" height="4" fill="#111"/>
            <rect x="28" y="22" width="4" height="4" fill="#111"/>
            <rect x="22" y="28" width="4" height="4" fill="#111"/>
            <rect x="28" y="28" width="4" height="4" fill="#111"/>
          </svg>
        </Box>
      </Box>

      {/* Bottom row: name/code + points */}
      <Box sx={{ position: "absolute", zIndex: 1, bottom: "14px", left: "16px", right: "16px", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <Box>
          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#fff", lineHeight: 1.2, letterSpacing: "-0.3px" }}>
            {user?.firstName} {user?.lastName}
          </Typography>
          <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.55)", fontFamily: "monospace", mt: "3px" }}>
            #{user?.membershipCode}
          </Typography>
        </Box>

        {showPoints && (
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontSize: "22px", fontWeight: 700, color: "#fff", lineHeight: 1, letterSpacing: "-0.8px", textShadow: "0 1px 3px rgba(0,0,0,0.2)" }}>
              {user?.pointsBalance?.toLocaleString() ?? "0"}
            </Typography>
            <Typography sx={{ fontSize: "10px", color: "rgba(255,255,255,0.65)", mt: "2px", letterSpacing: "0.5px" }}>
              POINTS
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function Home({ user, onQrClick }: HomeProps) {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const { storeSettings } = useStoreSettings();
  const [receiptScanOpen, setReceiptScanOpen] = useState(false);
  const [referOpen, setReferOpen] = useState(false);
  const [referCopied, setReferCopied] = useState(false);
  const pointsEnabled = storeSettings?.pointsEnabled !== false;
  const stampsEnabled = storeSettings?.stampsEnabled === true;



  const { data: stampStatus, isLoading: stampLoading } = useQuery<StampStatus>({
    queryKey: ["stampStatus"],
    queryFn: fetchStampStatus,
    enabled: stampsEnabled,
    refetchInterval: 30_000,
  });

  const { data: couponsData, isLoading: couponsLoading } = useQuery<Coupon[]>({
    queryKey: ["memberCoupons"],
    queryFn: async () => {
      const d = await fetchMemberCoupons();
      return Array.isArray(d) ? d.slice(0, 6) : [];
    },
    refetchInterval: 30_000,
  });

  const { data: featuredPosts } = useQuery<FeaturedInstagramPost[]>({
    queryKey: ["instagramFeatured"],
    queryFn: fetchFeaturedInstagramPosts,
    staleTime: 5 * 60_000,
  });

  const activeCoupons = couponsData ?? [];
  const dataLoading = (stampsEnabled ? stampLoading : false) || couponsLoading;

  const getReferralReward = () => {
    const rs = storeSettings?.referralSettings;
    if (!rs) return "";
    if (rs.referrerPointsEnabled) return t("home.referFriend.rewardValue.points", { amount: rs.referrerPointsAmount });
    return t("home.referFriend.rewardValue.coupon");
  };

  const nextLevel = user.nextMembershipLevel;
  const hasUpgrade = nextLevel && nextLevel.requiredSpendingAmountToNextLevel > 0;
  const stampsReady = stampsEnabled && stampStatus?.stampEnabled !== false && !!stampStatus;

  // ── Home page content ──────────────────────────────────────────────────────
  const content = (
    <Box sx={{ flex: 1, backgroundColor: "#fff", display: "flex", flexDirection: "column", pb: '30px' }}>

      {/* Header */}
      <Box component="header" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, pt: "8px", pb: "14px", position: "sticky", top: 0, zIndex: 10, backgroundColor: "#fff" }}>
        <Box sx={{ width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {storeSettings?.logoUrl
            ? <img src={storeSettings.logoUrl} alt={storeSettings.text || "Logo"} style={{ maxWidth: 36, maxHeight: 36, objectFit: "contain" }} />
            : (
              <svg viewBox="0 0 48 48" width="32" height="32" fill="none">
                <path d="M24 6c1.5 6 4 10 8 13-4 1-7 4-8 9-1-5-4-8-8-9 4-3 6.5-7 8-13z" stroke="#3A3A3A" strokeWidth="1.4" fill="none"/>
                <path d="M24 28c0 6-2 10-6 14M24 28c0 6 2 10 6 14" stroke="#3A3A3A" strokeWidth="1.4" strokeLinecap="round"/>
                <path d="M16 20c-4 2-7 6-8 11M32 20c4 2 7 6 8 11" stroke="#3A3A3A" strokeWidth="1.2" strokeLinecap="round" opacity="0.7"/>
                <circle cx="24" cy="20" r="1.6" fill="#3A3A3A"/>
              </svg>
            )}
        </Box>
        <LanguageSelector />
      </Box>

      {/* Membership card */}
      <Box sx={{ px: 2 }}>
        <WalletPassCard
          user={user}
          stripUrl={storeSettings?.passSettings?.stripUrl}
          showPoints={pointsEnabled}
          onQrCodeClick={onQrClick}
        />
      </Box>

      {/* Progress block */}
      {pointsEnabled && (
        <Box sx={{ px: 2, mt: "10px", mb: "4px" }}>
          {hasUpgrade ? (
            <>
              <Box sx={{ height: 5, background: "#E8E8E8", borderRadius: "99px", overflow: "hidden", mb: "10px" }}>
                <Box sx={{ height: "100%", width: `${nextLevel!.progressToNextLevel}%`, background: "#7BA88A", borderRadius: "99px" }} />
              </Box>
              <Typography
                component="p"
                sx={{ fontSize: "13px", color: "#555", lineHeight: 1.4 }}
                dangerouslySetInnerHTML={{
                  __html: t("home.progressToNextLevel", {
                    amount: `<strong style="color:#7BA88A;font-weight:700;">HK$${nextLevel!.requiredSpendingAmountToNextLevel.toLocaleString()}</strong>`,
                    nextLevelName: nextLevel!.nextMembershipLevelName,
                  }),
                }}
              />
            </>
          ) : null}
        </Box>
      )}

      {/* Stamp card section */}
      {dataLoading && stampsEnabled && (
        <Box sx={{ mt: "12px", mx: 2, background: "#fff", borderRadius: "14px", border: "1px solid #E8E8E8", p: "14px" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: "10px" }}>
            <Skeleton variant="text" width={90} height={22} />
            <Skeleton variant="text" width={50} height={22} />
          </Box>
          <Skeleton variant="rounded" height={110} sx={{ borderRadius: "12px" }} />
        </Box>
      )}
      {!dataLoading && stampsReady && stampStatus && (
        <Box
          component="section"
          aria-label="Stamp Card"
          sx={{ mt: "12px", mx: 2, background: "#fff", borderRadius: "14px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid #E8E8E8", p: "14px" }}
        >
          {/* strip header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "10px" }}>
            <Typography component="h2" sx={{ fontSize: "15px", fontWeight: 700, color: "#1A1A1A", m: 0 }}>{t("home.stampCard")}</Typography>
          </Box>

          {/* stamp card article */}
          <Box sx={{ background: "linear-gradient(160deg,#FFF8F3 0%,#FFEFE6 55%,#FFF 100%)", border: "1px solid #F3C4B0", borderRadius: "12px", p: "12px 12px 10px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px", mb: "10px" }}>
              <Box>
                {stampStatus.rewardDesc && (
                  <Typography sx={{ mt: "3px", fontSize: "12px", color: "#8A8A8A", lineHeight: 1.35 }}>
                    {stampStatus.rewardDesc}
                  </Typography>
                )}
              </Box>
              <Box sx={{ flexShrink: 0, fontSize: "12px", fontWeight: 700, color: "#E85A22", background: "#FDEEE4", borderRadius: "99px", px: "9px", py: "4px", lineHeight: 1 }}>
                {stampStatus.accumulatedStamps} / {stampStatus.stampsToRedeem}
              </Box>
            </Box>

            <StampGrid filled={stampStatus.accumulatedStamps} total={stampStatus.stampsToRedeem} />

            {stampStatus.rewardDesc && (
              <Typography sx={{ mt: "10px", fontSize: "12px", color: "#666", lineHeight: 1.4, textAlign: "center" }}
                dangerouslySetInnerHTML={{ __html: t("common.moreStamps", {
                  count: Math.max(0, stampStatus.stampsToRedeem - stampStatus.accumulatedStamps),
                  reward: `<strong style="color:#6A9678;font-weight:700;">${stampStatus.rewardDesc}</strong>`,
                }) }}
              />
            )}
          </Box>
        </Box>
      )}

      {/* My Coupons strip */}
      {dataLoading && (
        <Box sx={{ mt: "8px", mx: 2, background: "#fff", borderRadius: "14px", border: "1px solid #E8E8E8", py: "14px", px: "14px" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: "10px" }}>
            <Skeleton variant="text" width={100} height={22} />
            <Skeleton variant="text" width={50} height={22} />
          </Box>
          <Box sx={{ display: "flex", gap: "10px" }}>
            {[0,1,2].map(i => <Skeleton key={i} variant="rounded" width={156} height={74} sx={{ borderRadius: "8px", flexShrink: 0 }} />)}
          </Box>
        </Box>
      )}
      <Box
        component="section"
        aria-label="My Coupons"
        sx={{ display: dataLoading ? "none" : undefined, mt: "8px", mx: 2, background: "#fff", borderRadius: "14px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid #E8E8E8", py: "14px", overflow: "hidden" }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: "14px", mb: "10px" }}>
          <Typography component="h2" sx={{ fontSize: "15px", fontWeight: 700, color: "#1A1A1A", m: 0 }}>
            {t("home.coupons.title")}
          </Typography>
          <SeeAllBtn onClick={() => navigate(routes.coupons)} label={t("common.seeAll")} />
        </Box>

        {activeCoupons.length > 0 ? (
          <Box sx={{
            display: "flex", gap: "10px",
            ml: "14px",
            overflowX: "auto", overflowY: "hidden",
            pl: "15px", pr: "14px", pb: "6px", pt: "2px",
            scrollSnapType: "x proximity",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}>
            {activeCoupons.map((coupon, idx) => (
              <HomeCouponTicket
                key={coupon.couponId}
                coupon={coupon}
                onClick={() => navigate(routes.couponDetail, { state: { coupons: activeCoupons, selectedIndex: idx } })}
              />
            ))}
          </Box>
        ) : (
          <Typography sx={{ px: "14px", fontSize: "13px", color: "#8A8A8A" }}>
            {t("home.coupons.empty")}
          </Typography>
        )}
      </Box>

      {/* Referral row */}
      <Box sx={{ mt: "14px", mx: 2, background: "#fff", border: "1px solid #E5E5E5", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        <Box
          role="button"
          tabIndex={0}
          onClick={() => { setReferOpen(o => !o); setReferCopied(false); }}
          sx={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            px: 2, py: "14px", cursor: "pointer",
            "&:active": { background: "#FAFAFA" },
          }}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#1A1A1A" }}>
            {t("home.referFriend.title")}
          </Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#7BA88A" }}>
            {t("home.referFriend.get")} {getReferralReward()}
          </Typography>
        </Box>
        {referOpen && user.referralCode && (
          <Box sx={{ px: 2, pb: "16px", borderTop: "1px solid #F0F0F0" }}>
            <Box sx={{ display: "flex", gap: "10px", mt: "14px" }}>
              <Box sx={{
                flex: 1, border: "1px solid #E0E0E0", borderRadius: "10px",
                px: "14px", py: "12px", fontSize: "16px", fontWeight: 700,
                color: "#1A1A1A", letterSpacing: "1px", background: "#FAFAFA",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {user.referralCode}
              </Box>
              <Box
                component="button"
                onClick={() => {
                  navigator.clipboard.writeText(user.referralCode!);
                  setReferCopied(true);
                  setTimeout(() => setReferCopied(false), 2000);
                }}
                sx={{
                  px: "20px", py: "12px", borderRadius: "10px", border: "none",
                  background: "#E85A22", color: "#fff", fontSize: "15px", fontWeight: 700,
                  cursor: "pointer", fontFamily: "inherit", flexShrink: 0,
                  "&:active": { opacity: 0.85 },
                }}
              >
                {referCopied ? t("referFriend.copied") : t("referFriend.copy")}
              </Box>
            </Box>
            <Typography sx={{ mt: "10px", fontSize: "13px", color: "#8A8A8A", textAlign: "center" }}>
              {t("home.referFriend.copyInstruction")}
            </Typography>
            <Typography
              onClick={() => setReferOpen(false)}
              sx={{ mt: "12px", fontSize: "16px", fontWeight: 700, color: "#E85A22", textAlign: "center", cursor: "pointer", textDecoration: "underline" }}
            >
              {t("common.done")}
            </Typography>
          </Box>
        )}
      </Box>

      {/* News section */}
      {(featuredPosts === undefined || featuredPosts.length > 0) && (
        <Box component="section" aria-label="News" sx={{ mt: "20px", mx: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: "12px", px: "2px" }}>
            <Typography component="h2" sx={{ fontSize: "15px", fontWeight: 700, color: "#1A1A1A", m: 0 }}>{t("home.news")}</Typography>
            <SeeAllBtn onClick={() => navigate(routes.news)} label={t("common.seeAll")} />
          </Box>
          <Box sx={{
            display: "flex", gap: "12px",
            overflowX: "auto", overflowY: "hidden",
            pb: "8px", pt: "2px", mx: "-2px", px: "2px",
            scrollSnapType: "x proximity",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}>
            {(featuredPosts ?? []).map((post) => (
              <Box
                key={post.id}
                component="a"
                href={post.permalink}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ flexShrink: 0, width: 152, scrollSnapAlign: "start", cursor: "pointer", transition: "transform 0.12s ease", textDecoration: "none", "&:active": { transform: "scale(0.97)" } }}
              >
                <Box sx={{ position: "relative", width: 152, height: 152, borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 10px rgba(0,0,0,0.08)", mb: "8px", background: "#E8E8E8" }}>
                  <img
                    src={post.thumbnailUrl ?? post.mediaUrl ?? ""}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                  <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.35) 0%, transparent 50%)", pointerEvents: "none" }} />
                  {post.badgeLabel && (
                    <Box sx={{ position: "absolute", top: "8px", left: "8px", background: "rgba(0,0,0,0.55)", borderRadius: "6px", px: "6px", py: "3px" }}>
                      <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#fff", lineHeight: 1 }}>{post.badgeLabel}</Typography>
                    </Box>
                  )}
                </Box>
                {post.caption && (
                  <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "#555", lineHeight: 1.35, px: "2px", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {post.caption}
                  </Typography>
                )}
              </Box>
            ))}
            {!featuredPosts && [0,1,2,3].map(i => (
              <Box key={i} sx={{ flexShrink: 0, width: 152 }}>
                <Skeleton variant="rounded" width={152} height={152} sx={{ borderRadius: "12px", mb: "8px" }} />
                <Skeleton variant="text" width={140} height={14} />
                <Skeleton variant="text" width={100} height={14} />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Menu list */}
      <Box
        component="nav"
        aria-label="Member menu"
        sx={{ mt: "22px", mb: "8px", mx: 2, background: "#FAFAFA", borderRadius: "12px", border: "1px solid #EFEFEF", overflow: "hidden" }}
      >
        {[
          {
            label: t("home.menu.uploadReceipt"),
            icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="none"><path d="M7 3.5h10a1 1 0 0 1 1 1V20l-2-1.2L14 20l-2-1.2L10 20l-2-1.2L6 20V4.5a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M9 8h6M9 11.5h6M9 15h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
            onClick: () => setReceiptScanOpen(true),
          },
          {
            label: t("home.menu.myWallet"),
            icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="none"><path d="M3.5 8.5h17a1 1 0 0 1 1 1v8a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2v-8a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M3.5 8.5V7a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2v1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M15.5 13.5h3.5v2H15.5a1 1 0 0 1 0-2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
            onClick: () => navigate(routes.wallet),
          },
          {
            label: t("home.menu.memberPrivileges"),
            icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="none"><path d="M12 3.5L19.5 12 12 20.5 4.5 12 12 3.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M12 7.5L16.2 12 12 16.5 7.8 12 12 7.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg>,
            onClick: () => navigate(routes.memberPrivileges),
          },
          {
            label: t("home.menu.transactionHistory"),
            icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="none"><path d="M19.5 12a7.5 7.5 0 1 1-2.1-5.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M19.5 5.5v4h-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 8.5V12l2.5 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
            onClick: () => navigate(routes.transactionHistory),
          },
          {
            label: t("home.menu.bookmark"),
            icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="none"><path d="M12 3.8l2.2 4.5 5 .7-3.6 3.5.9 5L12 15.2 7.5 17.5l.9-5L4.8 9l5-.7L12 3.8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
            onClick: () => {},
          },
          {
            label: t("home.menu.editProfile"),
            icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="none"><circle cx="12" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.5"/><path d="M5.5 19c1.2-3.2 3.5-4.8 6.5-4.8s5.3 1.6 6.5 4.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
            onClick: () => navigate(routes.account),
          },
        ].map((item, i, arr) => (
          <Box
            key={item.label}
            component="button"
            type="button"
            onClick={item.onClick}
            sx={{
              display: "flex", alignItems: "center", gap: "14px",
              width: "100%", px: "14px", py: "15px",
              background: "transparent",
              border: "none",
              borderBottom: i < arr.length - 1 ? "1px solid #E8E8E8" : "none",
              cursor: "pointer", fontFamily: "inherit", textAlign: "left",
              color: "#3A3A3A", transition: "background 0.12s ease",
              "&:active": { background: "#F0F0F0" },
            }}
          >
            <Box sx={{ color: "#C4A574", display: "flex", alignItems: "center", flexShrink: 0 }}>{item.icon}</Box>
            <Box component="span" sx={{ flex: 1, fontSize: "15px", fontWeight: 500, color: "#3A3A3A", lineHeight: 1.3 }}>{item.label}</Box>
            <Box component="span" sx={{ fontSize: "18px", fontWeight: 300, color: "#C0C0C0", lineHeight: 1 }}>›</Box>
          </Box>
        ))}
      </Box>

    </Box>
  );

  return (
    <>
      {content}
      <ReceiptScanModal open={receiptScanOpen} onClose={() => setReceiptScanOpen(false)} />
    </>
  );
}
